import {reset, startSubmit, stopSubmit} from "redux-form";
import {eventChannel} from "redux-saga";
import {all, call, cancel, cancelled, fork, put, select, take,} from "redux-saga/effects";
import {Company} from "../../domain/model/company";
import {Message} from "../../domain/model/message";
import {MessageRoom} from "../../domain/model/message_room";
import {Project} from "../../domain/model/project";
import {User} from "../../domain/model/user";
import {ICompanyRepository} from "../../domain/repository/company_repository";
import {IMessageRoomRepository} from "../../domain/repository/message_room_repository";
import {IProjectRepository} from "../../domain/repository/project_repository";
import {createChatUsecase, IChatUseCase,} from "../../domain/usecase/chat_usecase";
import {createFileUsecase, IFileUseCase,} from "../../domain/usecase/file_usecase";
import {createApiClient, IApiClient} from "../../infra/api/client";
import {createCompanyAPI} from "../../infra/api/service/company_api";
import {createMessageRoomAPI} from "../../infra/api/service/message_room_api";
import {createProjectAPI} from "../../infra/api/service/project_api";
import {
    createCustomerMessageRoomActionCreator,
    CustomerMessageRoomActionType,
    ICustomerMessageRoomActionCreator,
    IRequestCreateMessageRoomAction,
    IRequestSendMessageRoomAction,
} from "../action/customer_message_room_action";
import {
    CreateMessageRoomByCustomerResponse,
    ObserveMessageRoomByCustomerResponse,
} from "../response/message_room_response";
import {AppState} from "../store/app_state";
import {AuthState} from "../store/auth_state";
import {TMessageSignature} from "../type/common";
import {handleErrorForHandler} from "./handleErrorForHandler";

const apiClient: IApiClient = createApiClient();
const projectRepository: IProjectRepository = createProjectAPI(apiClient);
const companyRepository: ICompanyRepository = createCompanyAPI(apiClient);
const messageRoomRepository: IMessageRoomRepository =
    createMessageRoomAPI(apiClient);
const chatUseCase: IChatUseCase = createChatUsecase();
const fileUseCase: IFileUseCase = createFileUsecase();
const actionCreator: ICustomerMessageRoomActionCreator =
    createCustomerMessageRoomActionCreator();

let task: any;

function* handleCreateMessageRoomInCustomerMessageRoom() {
    while (true) {
        try {
            const action: IRequestCreateMessageRoomAction = yield take(
                CustomerMessageRoomActionType.REQUEST_CREATE_MESSAGE_ROOM
            );
            const authSelector = (state: AppState) => state.authReducer;
            const _authState: AuthState = yield select(authSelector);
            const user: User | null = _authState.user;
            let ownSignature: TMessageSignature = {id: "", name: "", iconURL: ""};
            if (_authState.user && _authState.user.customer) {
                ownSignature = {
                    id: _authState.user.customer.id,
                    name: _authState.user.customer.name,
                    iconURL: _authState.user.customer.getIconURL(),
                    description: _authState.user.customer.pr,
                };
            }

            let messagesOrigin: Message[];
            let messageRoom: MessageRoom;
            let project: Project;
            let company: Company;
            [messagesOrigin, messageRoom, project, company] = yield all([
                call(
                    createMessageRoomDetail,
                    user,
                    action.item.projectId,
                    action.item.opponentId
                ),
                call(getMessageRoom, action.item.projectId, action.item.opponentId),
                call(getProject, action.item.projectId),
                call(getMaskCompany, action.item.opponentId, action.item.projectId),
                call(read, action.item.projectId, action.item.opponentId),
            ]);
            const messages = messagesOrigin.concat();
            const opponentSignature: TMessageSignature = {
                id: company.id,
                name: company.name,
                iconURL: company.getLogoURL(),
                description: company.introduction,
            };

            const res: CreateMessageRoomByCustomerResponse =
                new CreateMessageRoomByCustomerResponse();
            res.messageRoom = messageRoom;
            res.messages = messages;
            res.project = project;
            res.ownSignature = ownSignature;
            res.opponentSignature = opponentSignature;
            task = yield fork(watchMessages);
            yield put(actionCreator.callbackCreateMessageRoomAction(true, res));
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(
                actionCreator.callbackCreateMessageRoomAction(
                    false,
                    undefined,
                    error.toString()
                )
            );
        }
    }
}

function* handleSendMessageRoomInCustomerMessageRoom() {
    while (true) {
        try {
            const action: IRequestSendMessageRoomAction = yield take(
                CustomerMessageRoomActionType.REQUEST_SEND_MESSAGE_ROOM
            );
            yield put(startSubmit("customerMessageRoomForm"));
            const imageUrl: string = "";
            const authSelector = (state: AppState) => state.authReducer;
            const _authState: AuthState = yield select(authSelector);
            const user: User | null = _authState.user;
            const fileUrl: string = action.item.uploadFile
                ? yield call(
                    createMessageFile,
                    user,
                    action.item.uploadFile,
                    action.item.projectId,
                    action.item.opponentId
                )
                : "";
            yield call(
                sendMessageRoom,
                user,
                action.item.text,
                imageUrl,
                fileUrl,
                action.item.opponentId
            );
            yield put(actionCreator.callbackSendMessageRoomAction(true));
            yield put(stopSubmit("customerMessageRoomForm"));
            yield put(reset("customerMessageRoomForm"));
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(
                stopSubmit("customerMessageRoomForm", {_error: error.toString()})
            );
        }
    }
}

function* handleDestroyMessageRoomInCustomerMessageRoom() {
    while (true) {
        try {
            yield take(CustomerMessageRoomActionType.REQUEST_DESTROY_MESSAGE_ROOM);
            if (task) {
                yield cancel(task);
            }
            yield put(actionCreator.callbackDestroyMessageRoomAction(true));
        } catch (error) {
            yield fork(handleErrorForHandler, error);
            yield put(
                actionCreator.callbackDestroyMessageRoomAction(false, error.toString())
            );
        }
    }
}

function* messageChannel() {
    return eventChannel((emit) => {
        chatUseCase.observeMessage((message) => {
            emit(message);
        });

        const unsubscribe = () => {
            chatUseCase.closeRoom();
        };

        return unsubscribe;
    });
}

function* watchMessages() {
    const messages = yield call(messageChannel);
    try {
        while (true) {
            const message = yield take(messages);
            const res: ObserveMessageRoomByCustomerResponse =
                new ObserveMessageRoomByCustomerResponse();
            res.message = message;
            yield put(actionCreator.callbackObserveMessageRoomAction(true, res));
        }
    } catch (error) {

    } finally {
        if (yield cancelled()) {
            messages.close();
        }
    }
}

const getMessageRoom = (
    projectID: string,
    opponentID: string
): Promise<MessageRoom> => {
    return messageRoomRepository.get(projectID, opponentID);
};

const getProject = (projectId: string): Promise<Project> => {
    return projectRepository.get(projectId);
};

const getMaskCompany = (
    companyId: string,
    projectId: string
): Promise<Company> => {
    return companyRepository.getWithMask(companyId, projectId);
};

const createMessageRoomDetail = (
    user: User,
    projectId: string,
    opponentId: string
) => {
    return chatUseCase.connectRoom(user, projectId, opponentId);
};

const sendMessageRoom = (
    user: User,
    text: string,
    imageUrl: string,
    fileUrl: string,
    toId: string
) => {
    return chatUseCase.sendMessage(user, text, imageUrl, fileUrl, toId);
};

const read = (projectId: string, opponentId: string): Promise<void> => {
    return messageRoomRepository.read(projectId, opponentId);
};

const createMessageFile = (
    user: User,
    uploadFile: File,
    projectId: string,
    opponentId: string
): Promise<string> => {
    return fileUseCase.createMessageFile(user, uploadFile, projectId, opponentId);
};

export {
    handleCreateMessageRoomInCustomerMessageRoom,
    handleSendMessageRoomInCustomerMessageRoom,
    handleDestroyMessageRoomInCustomerMessageRoom,
};



// WEBPACK FOOTER //
// ./src/presentation/handler/customer_message_room_handler.ts